import { useState } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Menu, FolderPlus } from "lucide-react";

export default function AdminImport() {
  const { authAxios } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        setResult(null);
      }
    }
  };

  const validateFile = (f) => {
    const validExtensions = ['.xls', '.xlsx', '.csv'];
    const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) {
      toast.error("Formato file non supportato. Usa .xls, .xlsx o .csv");
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        setResult(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Seleziona un file da importare");
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authAxios.post(
        `/products/import?update_existing=${updateExisting}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setResult(response.data);
      
      if (response.data.imported > 0 || response.data.updated > 0) {
        toast.success(`Importazione completata! ${response.data.imported} nuovi, ${response.data.updated} aggiornati`);
      } else {
        toast.info("Nessun prodotto importato");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'importazione");
      setResult({
        total_rows: 0,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: [error.response?.data?.detail || "Errore sconosciuto"],
        categories_created: []
      });
    } finally {
      setUploading(false);
    }
  };

  const totalProcessed = result ? result.imported + result.updated + result.skipped : 0;
  const successRate = result && result.total_rows > 0 
    ? Math.round(((result.imported + result.updated) / result.total_rows) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Importa" />
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Importa Prodotti</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Importa prodotti dal tuo gestionale</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Card */}
          <Card className="bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-amber-500" />
                Carica File
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Formati supportati: .xls, .xlsx, .csv
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive 
                    ? "border-amber-500 bg-amber-500/10" 
                    : file 
                      ? "border-emerald-500/50 bg-emerald-500/5" 
                      : "border-white/10 hover:border-white/20"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {file ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-zinc-400 text-sm">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFile(null)}
                      className="text-zinc-400 hover:text-red-400"
                    >
                      Rimuovi
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 text-zinc-500 mx-auto" />
                    <div>
                      <p className="text-white mb-1">Trascina il file qui</p>
                      <p className="text-zinc-500 text-sm">oppure</p>
                    </div>
                    <label>
                      <input
                        type="file"
                        accept=".xls,.xlsx,.csv"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="border-white/10"
                        onClick={(e) => e.currentTarget.previousElementSibling.click()}
                      >
                        Seleziona File
                      </Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-white">Aggiorna prodotti esistenti</Label>
                  <p className="text-xs text-zinc-500">Se SKU esiste, aggiorna i dati</p>
                </div>
                <Switch
                  checked={updateExisting}
                  onCheckedChange={setUpdateExisting}
                  data-testid="update-existing-switch"
                />
              </div>

              {/* Import Button */}
              <Button
                onClick={handleImport}
                disabled={!file || uploading}
                data-testid="import-btn"
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full"></span>
                    Importazione in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Importa Prodotti
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Mapping Info Card */}
          <Card className="bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Mappatura Colonne</CardTitle>
              <CardDescription className="text-zinc-400">
                Come vengono mappati i dati dal tuo gestionale
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">ARTICOLO</span>
                  <span className="text-white font-mono">→ SKU</span>
                </div>
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">DescrArticolo</span>
                  <span className="text-white font-mono">→ Nome</span>
                </div>
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">CatMer</span>
                  <span className="text-white font-mono">→ Categoria</span>
                </div>
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">Um</span>
                  <span className="text-white font-mono">→ Unità</span>
                </div>
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">Prezzo1</span>
                  <span className="text-amber-400 font-mono">→ Prezzo Listino</span>
                </div>
                <div className="flex justify-between p-2 bg-white/5 rounded">
                  <span className="text-zinc-400">PrezzoAcq</span>
                  <span className="text-emerald-400 font-mono">→ Prezzo Acquisto</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-4">
                * Il prezzo minimo viene calcolato automaticamente (85% del listino)
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Results Card */}
        {result && (
          <Card className="mt-6 bg-[#18181b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {result.errors.length === 0 && (result.imported > 0 || result.updated > 0) ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : result.errors.length > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                Risultato Importazione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Progress */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Completamento</span>
                  <span className="text-white">{successRate}%</span>
                </div>
                <Progress value={successRate} className="h-2" />
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white font-mono">{result.total_rows}</p>
                  <p className="text-xs text-zinc-400">Righe Totali</p>
                </div>
                <div className="p-4 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-400 font-mono">{result.imported}</p>
                  <p className="text-xs text-zinc-400">Nuovi Prodotti</p>
                </div>
                <div className="p-4 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400 font-mono">{result.updated}</p>
                  <p className="text-xs text-zinc-400">Aggiornati</p>
                </div>
                <div className="p-4 bg-zinc-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-zinc-400 font-mono">{result.skipped}</p>
                  <p className="text-xs text-zinc-400">Saltati</p>
                </div>
              </div>

              {/* New Categories */}
              {result.categories_created.length > 0 && (
                <div>
                  <p className="text-sm text-zinc-400 mb-2 flex items-center gap-2">
                    <FolderPlus className="w-4 h-4" />
                    Nuove categorie create:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.categories_created.map((cat) => (
                      <Badge key={`cat-${cat}`} className="bg-amber-500/20 text-amber-400">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div>
                  <p className="text-sm text-red-400 mb-2">Errori ({result.errors.length}):</p>
                  <div className="max-h-40 overflow-y-auto bg-red-500/10 rounded-lg p-3">
                    {result.errors.slice(0, 10).map((err) => (
                      <p key={`err-${String(err).slice(0, 96)}`} className="text-xs text-red-300 mb-1">{err}</p>
                    ))}
                    {result.errors.length > 10 && (
                      <p className="text-xs text-red-400">...e altri {result.errors.length - 10} errori</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
